import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import type { Game } from "../data/games";
import {
  hostParticipant,
  simulatedParticipants,
} from "../data/simulatedParticipants";
import { createRoundResult } from "../game-engine/simulation";
import { generateSessionCode } from "../game-engine/sessionCode";
import type {
  GameDefinition,
  RoundDefinition,
  RoundResult,
  SimulatedParticipant,
} from "../game-engine/types";
import { HostClient } from "../net/hostClient";
import {
  applyEnvelope,
  initHostState,
  markHostAnswer,
  resetHostState,
  type HostUiState,
} from "../net/hostReducer";
import type { HostMode } from "../net/mode";
import type { Envelope } from "../net/types";

export type GameSessionPhase =
  | "lobby"
  | "countdown"
  | "question"
  | "answering"
  | "reveal"
  | "summary";

export interface GameSessionState {
  phase: GameSessionPhase;
  code: string;
  participants: SimulatedParticipant[];
  currentRoundIndex: number;
  answeredParticipantIds: string[];
  hostAnswerId: string | null;
  results: RoundResult[];
  runId: number;
  autoJoinStopped: boolean;
}

export interface GameSessionApi {
  state: GameSessionState;
  currentRound: RoundDefinition | undefined;
  currentResult: RoundResult | undefined;
  roundParticipants: SimulatedParticipant[];
  roundCount: number;
  countdown: number;
  joinAll: () => void;
  removeParticipant: (participantId: string) => void;
  startGame: () => void;
  answerAsHost: (answerId: string) => void;
  nextRound: () => void;
  resetSession: () => void;
}

type GameSessionAction =
  | { type: "participant-joined"; participant: SimulatedParticipant }
  | { type: "participant-left"; participantId: string }
  | { type: "all-participants-joined" }
  | { type: "game-started" }
  | { type: "participant-answered"; participantId: string }
  | { type: "host-answered"; answerId: string }
  | { type: "round-revealed"; result: RoundResult }
  | { type: "next-round"; isLastRound: boolean }
  | { type: "session-reset"; code: string };

function createInitialState(): GameSessionState {
  return {
    phase: "lobby",
    code: generateSessionCode(),
    participants: [],
    currentRoundIndex: 0,
    answeredParticipantIds: [],
    hostAnswerId: null,
    results: [],
    runId: 0,
    autoJoinStopped: false,
  };
}

function sessionReducer(
  state: GameSessionState,
  action: GameSessionAction,
): GameSessionState {
  switch (action.type) {
    case "participant-joined":
      if (
        state.phase !== "lobby" ||
        state.participants.some(
          (participant) => participant.id === action.participant.id,
        )
      ) {
        return state;
      }

      return {
        ...state,
        participants: [...state.participants, action.participant],
      };
    case "participant-left":
      return state.phase === "lobby"
        ? {
            ...state,
            participants: state.participants.filter(
              (participant) => participant.id !== action.participantId,
            ),
          }
        : state;
    case "all-participants-joined":
      return state.phase === "lobby"
        ? {
            ...state,
            participants: [...simulatedParticipants],
            autoJoinStopped: true,
          }
        : state;
    case "game-started":
      return state.phase === "lobby"
        ? { ...state, phase: "question" }
        : state;
    case "participant-answered":
      if (
        state.phase !== "answering" ||
        state.answeredParticipantIds.includes(action.participantId)
      ) {
        return state;
      }

      return {
        ...state,
        answeredParticipantIds: [
          ...state.answeredParticipantIds,
          action.participantId,
        ],
      };
    case "host-answered":
      if (
        (state.phase !== "question" && state.phase !== "answering") ||
        state.answeredParticipantIds.includes(hostParticipant.id)
      ) {
        return state;
      }

      return {
        ...state,
        phase: "answering",
        hostAnswerId: action.answerId,
        answeredParticipantIds: [
          ...state.answeredParticipantIds,
          hostParticipant.id,
        ],
      };
    case "round-revealed":
      if (state.phase !== "answering") {
        return state;
      }

      return {
        ...state,
        phase: "reveal",
        answeredParticipantIds: state.participants.map(
          (participant) => participant.id,
        ),
        results: [
          ...state.results.filter(
            (result) => result.roundId !== action.result.roundId,
          ),
          action.result,
        ],
      };
    case "next-round":
      if (state.phase !== "reveal") {
        return state;
      }

      return action.isLastRound
        ? { ...state, phase: "summary" }
        : {
            ...state,
            phase: "question",
            currentRoundIndex: state.currentRoundIndex + 1,
            answeredParticipantIds: [],
            hostAnswerId: null,
          };
    case "session-reset":
      return {
        ...createInitialState(),
        code: action.code,
        runId: state.runId + 1,
      };
  }
}

function useSimulationGameSession(definition: GameDefinition): GameSessionApi {
  const [state, dispatch] = useReducer(
    sessionReducer,
    undefined,
    createInitialState,
  );

  useEffect(() => {
    if (state.phase !== "lobby" || state.autoJoinStopped) {
      return;
    }

    const timers = simulatedParticipants.map((participant, index) =>
      window.setTimeout(
        () => dispatch({ type: "participant-joined", participant }),
        500 + index * 700,
      ),
    );

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [state.autoJoinStopped, state.phase, state.runId]);

  useEffect(() => {
    if (state.phase !== "answering") {
      return;
    }

    const timers = state.participants.map((participant, index) =>
      window.setTimeout(
        () =>
          dispatch({
            type: "participant-answered",
            participantId: participant.id,
          }),
        350 + index * 230,
      ),
    );

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [
    state.currentRoundIndex,
    state.participants,
    state.phase,
    state.runId,
  ]);

  const currentRound = definition.rounds[state.currentRoundIndex];
  const roundParticipants = useMemo(
    () => [hostParticipant, ...state.participants],
    [state.participants],
  );
  const currentResult = currentRound
    ? state.results.find((result) => result.roundId === currentRound.id)
    : undefined;

  const revealCurrentRound = useCallback(() => {
    if (!currentRound || !state.hostAnswerId) {
      return;
    }

    dispatch({
      type: "round-revealed",
      result: createRoundResult(
        currentRound,
        roundParticipants,
        state.currentRoundIndex,
        { [hostParticipant.id]: state.hostAnswerId },
      ),
    });
  }, [
    currentRound,
    roundParticipants,
    state.currentRoundIndex,
    state.hostAnswerId,
  ]);

  useEffect(() => {
    const everyoneHasAnswered =
      state.answeredParticipantIds.length === roundParticipants.length;

    if (
      state.phase !== "answering" ||
      !state.hostAnswerId ||
      !everyoneHasAnswered
    ) {
      return;
    }

    const revealTimer = window.setTimeout(revealCurrentRound, 420);

    return () => window.clearTimeout(revealTimer);
  }, [
    revealCurrentRound,
    roundParticipants.length,
    state.answeredParticipantIds.length,
    state.hostAnswerId,
    state.phase,
  ]);

  return {
    state,
    currentRound,
    currentResult,
    roundParticipants,
    roundCount: definition.rounds.length,
    countdown: 3,
    joinAll: () => dispatch({ type: "all-participants-joined" }),
    removeParticipant: (participantId: string) =>
      dispatch({ type: "participant-left", participantId }),
    startGame: () => dispatch({ type: "game-started" }),
    answerAsHost: (answerId: string) =>
      dispatch({ type: "host-answered", answerId }),
    nextRound: () =>
      dispatch({
        type: "next-round",
        isLastRound:
          state.currentRoundIndex === definition.rounds.length - 1,
      }),
    resetSession: () =>
      dispatch({ type: "session-reset", code: generateSessionCode() }),
  };
}

// ---- live mode (server-as-controller) ----

type LiveAction =
  | Envelope
  | { type: "host-answer"; optionId: string }
  | { type: "reset" };

function liveReducer(state: HostUiState, action: LiveAction): HostUiState {
  switch (action.type) {
    case "host-answer":
      // Envelope.type is `string`, so it overlaps this literal; cast to narrow.
      return markHostAnswer(state, (action as { optionId: string }).optionId);
    case "reset":
      return resetHostState(state);
    default:
      // All wire envelopes flow through applyEnvelope (including the synthetic
      // "ws-state" envelope used to mirror HostClient connection state).
      return applyEnvelope(state, action as Envelope);
  }
}

function useLiveGameSession(
  definition: GameDefinition,
  game: Game | undefined,
): GameSessionApi {
  const [host, dispatch] = useReducer(liveReducer, "", initHostState);
  const clientRef = useRef<HostClient | null>(null);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    startTimeRef.current = host.startTime;
  }, [host.startTime]);

  useEffect(() => {
    // Fresh host: no room code — the server creates one and replies
    // `room-created` with the assigned code + QR payload.
    const client = new HostClient({});
    client.onMessage((env) => dispatch(env));
    client.onState((s) =>
      dispatch({ type: "ws-state", payload: { connected: s === "open" } }),
    );
    client.open();
    clientRef.current = client;
    return () => {
      client.close();
      clientRef.current = null;
    };
  }, []);

  const sessionState: GameSessionState = {
    phase: host.phase,
    code: host.code,
    participants: host.participants,
    currentRoundIndex: host.currentRoundIndex,
    answeredParticipantIds: host.answeredParticipantIds,
    hostAnswerId: host.hostAnswerId,
    results: host.results,
    runId: host.runId,
    autoJoinStopped: true, // phones join themselves; "Anslut alla" is a no-op
  };

  const gameId = game?.id ?? definition.gameId;

  return {
    state: sessionState,
    currentRound: host.currentRound,
    currentResult: host.currentResult,
    roundParticipants: host.roundParticipants,
    roundCount: host.totalQuestions,
    countdown: host.countdown,
    joinAll: () => {
      /* phones join themselves via /join/:code */
    },
    removeParticipant: (playerId: string) =>
      clientRef.current?.send({ type: "host-kick", payload: { playerId } }),
    startGame: () =>
      clientRef.current?.send({ type: "host-start", payload: { gameId } }),
    answerAsHost: (optionId: string) => {
      dispatch({ type: "host-answer", optionId });
      const start = startTimeRef.current ?? Date.now();
      clientRef.current?.send({
        type: "answer",
        payload: { index: Number(optionId), time: Date.now() - start },
      });
    },
    nextRound: () =>
      clientRef.current?.send({ type: "host-next", payload: {} }),
    resetSession: () => {
      dispatch({ type: "reset" });
      clientRef.current?.send({ type: "host-reset", payload: {} });
    },
  };
}

export function useGameSession(
  definition: GameDefinition,
  game?: Game,
  mode: HostMode = "simulation",
): GameSessionApi {
  if (mode === "live") return useLiveGameSession(definition, game);
  return useSimulationGameSession(definition);
}